/**
 * PlaceCoreMapCard — Map preview with "Öppna karta" link.
 * Edge-to-edge on mobile.
 */
import { ExternalLink, MapPin } from "lucide-react";
import { useTranslate } from "@/i18n";

interface Props {
  mapTitle: string;
  mapSubtitle: string | null;
  hasCoordinates: boolean;
  googleMapsUrl: string;
}

export function PlaceCoreMapCard({ mapTitle, mapSubtitle, hasCoordinates, googleMapsUrl }: Props) {
  const t = useTranslate();
  if (!hasCoordinates && !mapTitle) return null;

  return (
    <div className="rounded-xl overflow-hidden border border-border/40">
      <div
        className="h-48 sm:h-56 lg:h-64 relative"
        style={{
          background:
            "linear-gradient(145deg, hsl(var(--secondary)) 0%, hsl(var(--muted)) 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cpath d='M20 180 Q60 140 100 150 T180 120' fill='none' stroke='%23556B2F' stroke-width='2'/%3E%3Cpath d='M0 100 Q40 80 80 95 T160 70 T200 80' fill='none' stroke='%23556B2F' stroke-width='1.5'/%3E%3Cpath d='M30 40 Q70 60 120 45 T200 50' fill='none' stroke='%23556B2F' stroke-width='1'/%3E%3C/svg%3E")`,
            backgroundSize: "200px 200px",
          }}
        />
        {hasCoordinates && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/15 rounded-full blur-2xl scale-[2.5]" />
              <div className="relative bg-primary text-primary-foreground p-3.5 rounded-full shadow-lg">
                <MapPin className="h-6 w-6" />
              </div>
            </div>
          </div>
        )}

        {/* Overlay CTA bottom-right */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background/90 text-foreground backdrop-blur-sm shadow-sm hover:bg-background transition-colors"
        >
          {t("place.map.openMap")}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="px-4 py-3 bg-card border-t border-border/30">
        <p className="text-sm font-medium text-foreground truncate">{mapTitle}</p>
        {mapSubtitle && (
          <p className="text-xs text-muted-foreground">{mapSubtitle}</p>
        )}
      </div>
    </div>
  );
}
